import { createTheme } from '@material-ui/core/styles'
import { ThemeOptions } from '@material-ui/core/styles/createTheme'
import {
  createTheme as createThemeMuiV5,
  PaletteOptions as PaletteOptionsMuiV5,
  ThemeOptions as ThemeOptionsMuiV5,
} from '@mui/material/styles'

import '@fontsource/roboto'
import '@fontsource/inter'

import { VehicleStateEnum } from 'src/api/provider-agency'

interface IMdsPalette {
  background: {
    filterMenu: React.CSSProperties['color']
    mainMenu: React.CSSProperties['color']
  }
}

// See https://material-ui.com/guides/typescript/ for more explanations
declare module '@material-ui/core/styles/createTheme' {
  interface ICustomPalette {
    disabled: string
    outlinedErrorHover: string
    progress: string
    success: { dark: string; light: string; main: string }
  }
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Theme {
    customPalette: ICustomPalette
    mdsPalette: IMdsPalette
  }
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface ThemeOptions {
    customPalette: ICustomPalette
    mdsPalette: IMdsPalette
  }
}

// MUI v5 theme declaration
declare module '@mui/material/styles' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Theme {
    mdsPalette: IMdsPalette
  }
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface ThemeOptions {
    mdsPalette: IMdsPalette
  }
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface PaletteOptions {
    background?: PaletteOptionsMuiV5['background']
    button?: {
      background?: string
      color?: string
    }
    divider?: string
    error?: PaletteOptionsMuiV5['error']
    home?: {
      infoCard: {
        background: string
        color: string
      }
    }
    primary?: PaletteOptionsMuiV5['primary']
    secondary?: PaletteOptionsMuiV5['secondary']
    table?: {
      evenLineBackground?: string
      oddLineBackground?: string
    }
    tooltip?: string
  }
}

export const colors = {
  black: '#192233',
  blackTransparent: 'rgba(0, 0, 0, 0.38)',
  blue: '#0171e4',
  blueLight: '#188aff',
  blueDark: '#0058b3',
  borderLight: '#E2E5EA',
  green: '#5CB85C',
  greenLight: '#7cc67c',
  greenDark: '#408040',
  greenCaribbean: '#00cfac',
  grey: '#9E9E9E',
  greyLight: '#ccc',
  greyBackground: '#F7F8FA',
  greyDark: '#616161',
  greyMuted: '#6B7280',
  greySubtle: '#9CA3AF',
  greyTransparent: 'rgba(222, 226, 235, 0.6)',
  orange: '#FFA03C',
  purple: '#6c2bd4',
  pink: '#FF50F9',
  pinkDark: '#F42FB8',
  red: '#DC3030',
  redLight: '#ffd7db',
  redDark: '#9a2121',
  slate: '#455A64',
  yellow: '#FFFC3C',
  white: '#FFFFFF',
  goldenYellow: '#FFCB37',
}

export const mdsColors = {
  subhead: '#788697',
  body: '#1F2020',
  background1: '#FFFFFF',
  background2: '#EBEDF3',
  bluePlato: '#0171E4',
  buttonBackground: '#ECF5FF',
}

export const deviceColors = {
  bicycle: colors.pinkDark,
  car: colors.blue,
  cargoBicycle: colors.green,
  deliveryBot: colors.grey,
  moped: colors.purple,
  other: colors.goldenYellow,
  scooter: colors.greenCaribbean,
}

const cornerRadius = {
  extraSmall: '4px',
  small: '8px',
  medium: '12px',
  large: '16px',
  extraLarge: '28px',
}

const themeOptions: ThemeOptions = {
  typography: {
    fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
    fontSize: 10,
    h2: {
      fontSize: '2rem',
      fontWeight: 'bold',
      letterSpacing: 0,
    },
    h4: {
      fontWeight: 'bold',
    },
    h5: {
      lineHeight: '19px',
      fontSize: 16,
      fontWeight: 600,
      letterSpacing: '0.15px',
    },
    h6: {
      lineHeight: '17px',
      fontSize: 14,
      fontWeight: 400,
    },
    subtitle1: {
      lineHeight: '16px',
      fontSize: 13,
      fontWeight: 400,
    },
    subtitle2: {
      lineHeight: '15px',
      fontSize: 12,
      fontWeight: 400,
    },
  },
  spacing: 4,
  palette: {
    primary: {
      light: colors.blueLight,
      main: colors.blue,
      dark: colors.blueDark,
    },
    secondary: {
      light: colors.grey,
      main: colors.slate,
      dark: colors.greyDark,
    },
    error: {
      light: colors.redLight,
      main: colors.red,
      dark: colors.redDark,
    },
    background: {
      default: colors.greyTransparent,
      paper: colors.white,
    },
  },
  customPalette: {
    disabled: colors.blackTransparent,
    success: {
      light: colors.greenLight,
      main: colors.green,
      dark: colors.greenDark,
    },
    outlinedErrorHover: colors.redLight,
    progress: colors.yellow,
  },
  mdsPalette: {
    background: {
      filterMenu: colors.white,
      mainMenu: colors.black,
    },
  },
  overrides: {
    MuiCard: {
      root: { borderRadius: cornerRadius.medium },
    },
    MuiSvgIcon: {
      root: {
        fontSize: '24px',
      },
    },
    MuiTooltip: {
      popper: { fontSize: '1rem' },
    },
  },
}

const themeMuiV5Options: ThemeOptionsMuiV5 = {
  typography: {
    fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
    fontSize: 10,
    h2: {
      fontSize: '2rem',
      fontWeight: 'bold',
      letterSpacing: 0,
    },
    h4: {
      fontWeight: 'bold',
    },
    h5: {
      lineHeight: '19px',
      fontSize: 16,
      fontWeight: 600,
      letterSpacing: '0.15px',
    },
    h6: {
      lineHeight: '17px',
      fontSize: 14,
      fontWeight: 400,
    },
    subtitle1: {
      lineHeight: '16px',
      fontSize: 13,
      fontWeight: 400,
    },
    subtitle2: {
      lineHeight: '15px',
      fontSize: 12,
      fontWeight: 400,
    },
  },
  spacing: 4,
  palette: {
    primary: {
      light: colors.blueLight,
      main: colors.blue,
      dark: colors.blueDark,
    },
    secondary: {
      light: colors.grey,
      main: colors.slate,
      dark: colors.greyDark,
    },
    error: {
      light: colors.redLight,
      main: colors.red,
      dark: colors.redDark,
    },
    background: {
      default: colors.greyTransparent,
      paper: colors.white,
    },
    button: {
      color: mdsColors.bluePlato,
      background: mdsColors.buttonBackground,
    },
    table: {
      oddLineBackground: '#F9FAFC',
      evenLineBackground: colors.white,
    },
    divider: mdsColors.background2,
    tooltip: mdsColors.subhead,
    text: {
      primary: '#333',
    },
    home: {
      infoCard: {
        background: 'rgba(253, 238, 0, 0.4)',
        color: 'rgba(245, 124, 0, 1)',
      },
    },
    success: {
      light: colors.greenLight,
      main: colors.green,
      dark: colors.greenDark,
    },
  },
  mdsPalette: {
    background: {
      filterMenu: colors.white,
      mainMenu: colors.black,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: cornerRadius.medium },
      },
    },
    MuiAlert: {
      styleOverrides: {
        // A filled alert picks the text color that contrasts most with its background, which is dark on our
        // light success green: keep it white like the other severities.
        filledSuccess: ({ theme }) => ({ color: theme.palette.common.white }),
        // Inter leaves the text ~2px above the icon, as for the buttons below: move 2px of MUI's default vertical
        // padding ('8px 0' for the message, 4px on top of the action, from @mui/material/Alert/Alert.js v5.18) to
        // the top. The alert height is unchanged.
        message: { paddingTop: 10, paddingBottom: 6 },
        action: { paddingTop: 6 },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({ padding: theme.spacing(6, 6, 4) }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({ padding: theme.spacing(0, 6, 6) }),
      },
    },
    MuiButton: {
      styleOverrides: {
        // Inter reserves room for descenders that uppercase labels don't have, which leaves them ~2px above
        // center: move 1px of the default vertical padding from the bottom to the top. The button height and
        // horizontal padding are unchanged.
        root: ({ ownerState }) => {
          // MUI's default vertical padding per size, copied from @mui/material/Button/Button.js (v5.18).
          // Must be updated if a MUI upgrade changes the button padding, or the buttons change height.
          const padding = { small: 4, medium: 6, large: 8 }[ownerState.size ?? 'medium']
          // Outlined buttons take 1px less padding to make room for their 1px border.
          const border = ownerState.variant === 'outlined' ? 1 : 0
          return { paddingTop: padding - border + 1, paddingBottom: padding - border - 1 }
        },
      },
    },
  },
}

const theme = createTheme(themeOptions)
const themeMuiV5 = createThemeMuiV5(themeMuiV5Options)
theme.shadows[1] = ['5px 5px 10px 0px rgba(0, 0, 0, 0.05)'].join(',')

export const DEVICE_STATUS_COLOR = {
  [VehicleStateEnum.available]: colors.green,
  [VehicleStateEnum.non_operational]: colors.orange,
  [VehicleStateEnum.on_trip]: colors.blue,
  [VehicleStateEnum.reserved]: colors.blue,
  [VehicleStateEnum.removed]: colors.red,
  [VehicleStateEnum.unknown]: colors.purple,
  [VehicleStateEnum.elsewhere]: colors.pink,
  [VehicleStateEnum.stopped]: colors.yellow,
}

export default theme
export { themeMuiV5 }
